import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Download, FileArchive } from 'lucide-react';
import { useImageCompress, type CompressionOptions } from '@/hooks/useImageCompress';
import type { UploadedImage } from '@/pages/ImageToolsHub';
import { BeforeAfterComparison } from './BeforeAfterComparison';
import { toast } from 'sonner';

interface CompressionToolProps {
  images: UploadedImage[];
}

export const CompressionTool: React.FC<CompressionToolProps> = ({ images }) => {
  const { isProcessing, progress, compressedImages, processImages, downloadAll } = useImageCompress();
  
  const [options, setOptions] = useState<CompressionOptions>({
    quality: 85,
    format: 'jpeg',
    mode: 'quality',
    targetFileSize: 500,
    removeExif: true,
  });

  const selectedImages = images.filter(img => img.selected);

  const handleProcess = async () => {
    if (selectedImages.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    await processImages(selectedImages, options);
    toast.success(`Compressed ${selectedImages.length} image(s)`);
  };

  const handleDownloadAll = () => {
    downloadAll(compressedImages, 'compressed');
    toast.success('Downloaded all compressed images');
  };

  const totalOriginalSize = compressedImages.reduce((sum, img) => sum + img.originalSize, 0);
  const totalCompressedSize = compressedImages.reduce((sum, img) => sum + img.compressedSize, 0);
  const totalSavings = totalOriginalSize > 0 
    ? ((totalOriginalSize - totalCompressedSize) / totalOriginalSize) * 100 
    : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Compression Options</CardTitle>
          <CardDescription>
            Optimize images for web or reduce file size
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs value={options.mode} onValueChange={(value) => setOptions({ ...options, mode: value as any })}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="quality">Quality Mode</TabsTrigger>
              <TabsTrigger value="filesize">Target Size</TabsTrigger>
            </TabsList>

            <TabsContent value="quality" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Quality: {options.quality}%</Label>
                <Slider
                  value={[options.quality]}
                  onValueChange={([value]) => setOptions({ ...options, quality: value })}
                  min={1}
                  max={100}
                  step={1}
                />
                <p className="text-xs text-muted-foreground">
                  {options.quality < 50 && 'Low quality - Small file size'}
                  {options.quality >= 50 && options.quality < 85 && 'Good quality - Balanced'}
                  {options.quality >= 85 && 'High quality - Larger file size'}
                </p>
              </div>
            </TabsContent>

            <TabsContent value="filesize" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Target File Size (KB)</Label>
                <Input
                  type="number"
                  value={options.targetFileSize || ''}
                  onChange={(e) => setOptions({ ...options, targetFileSize: parseInt(e.target.value) || 500 })}
                  min={10}
                  max={10000}
                />
                <p className="text-xs text-muted-foreground">
                  The compressor will try to achieve this file size (approximate)
                </p>
              </div>
            </TabsContent>
          </Tabs>

          {/* Output Format */}
          <div className="space-y-2">
            <Label>Output Format</Label>
            <Select
              value={options.format}
              onValueChange={(value) => setOptions({ ...options, format: value as any })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="jpeg">JPEG (Best compression)</SelectItem>
                <SelectItem value="webp">WebP (Modern, smaller)</SelectItem>
                <SelectItem value="png">PNG (Lossless)</SelectItem>
              </SelectContent>
            </Select>
            {options.format === 'webp' && (
              <p className="text-xs text-muted-foreground">
                WebP offers better compression than JPEG with similar quality
              </p>
            )}
            {options.format === 'png' && (
              <p className="text-xs text-muted-foreground">
                PNG compression is lossless but produces larger files
              </p>
            )}
          </div>

          {/* Remove EXIF */}
          <div className="flex items-center justify-between">
            <div>
              <Label>Remove EXIF Metadata</Label>
              <p className="text-xs text-muted-foreground">Reduces file size and privacy</p>
            </div>
            <Switch
              checked={options.removeExif}
              onCheckedChange={(checked) => setOptions({ ...options, removeExif: checked })}
            />
          </div>

          {/* Process Button */}
          <Button
            onClick={handleProcess}
            disabled={isProcessing || selectedImages.length === 0}
            className="w-full"
          >
            {isProcessing ? 'Compressing...' : `Compress ${selectedImages.length} Image(s)`}
          </Button>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">{Math.round(progress)}%</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics */}
      {compressedImages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Compression Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold">{totalOriginalSize.toFixed(0)} KB</p>
                <p className="text-xs text-muted-foreground">Original Size</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{totalCompressedSize.toFixed(0)} KB</p>
                <p className="text-xs text-muted-foreground">Compressed Size</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{totalSavings.toFixed(1)}%</p>
                <p className="text-xs text-muted-foreground">Space Saved</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {compressedImages.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Compressed Images</CardTitle>
                <CardDescription>{compressedImages.length} images compressed</CardDescription>
              </div>
              <Button onClick={handleDownloadAll}>
                <Download className="h-4 w-4 mr-2" />
                Download All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {compressedImages.map((image) => (
              <div key={image.id} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {image.originalSize.toFixed(0)} KB → {image.compressedSize.toFixed(0)} KB
                  </span>
                  <span className="text-green-600 font-medium">
                    {image.compressionRatio.toFixed(1)}% saved
                  </span>
                </div>
                <BeforeAfterComparison
                  beforeImage={image.original}
                  afterImage={image.compressed}
                  beforeLabel="Original"
                  afterLabel="Compressed"
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
